import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import axiosInstance from '../axiosConfig';
import { useGlobalStore } from '../store/globalStore';
import { useDialogStore } from '../store/dialogStore';
import tournamentsEmptyState from '../assets/images/tournaments-item.svg';
import DoneIcon from '../assets/images/done.svg';

type EmailItem = {
  anmeldung_id: number;
  club_number_country: number;
  club_id: number;
  recipients: string[];
  from: string;
  attachment: string;
  entry: string;
  subject: string;
  body: string;
};

const formatDateTimeLocal = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const ResendEmailPage: React.FC = () => {
  const now = new Date();
  const todayStart = new Date(now.setHours(0, 0, 0, 0));
  const todayEnd = new Date(now.setHours(23, 59, 59, 999));
  const setLoading = useGlobalStore((state) => state.setLoading);
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);

  const [startDateTime, setStartDateTime] = useState<string>(
    formatDateTimeLocal(todayStart),
  );
  const [endDateTime, setEndDateTime] = useState<string>(
    formatDateTimeLocal(todayEnd),
  );
  const [clubId, setClubId] = useState<string>('');
  const [tournamentId, setTournamentId] = useState<string>('');

  const [results, setResults] = useState<EmailItem[]>([]);

  const [disabledEmails, setDisabledEmails] = useState<Set<number>>(new Set());

  const [groupDisabled, setGroupDisabled] = useState<boolean>(false);

  const handleSearch = () => {
    setResults([]);
    setDisabledEmails(new Set());
    setGroupDisabled(false);
    getList();
  };

  const handleSendEmail = (item: EmailItem) => {
    openDialog(
      <div className="text-md mt-10">
        <div className="px-5 my-4 text-sm">
          Möchten Sie die E-Mail:
          <b className="text-red-600"> {item.subject} </b>
          erneut an die folgenden E-Mail-Adressen senden?
        </div>
        <div className="mx-5 border shadow-sm mb-4 border-gray-300 rounded-md p-3 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {item.recipients.map((email: string) => (
              <div key={email} className="text-black font-medium">
                ✉️ {email}
              </div>
            ))}
          </div>
        </div>
      </div>,
      () => {
        resendEmailAPIcall([item]);
        setDisabledEmails((prev) => new Set(prev).add(item.anmeldung_id));
        closeDialog();
      },
    );
  };

  const handleSendAll = () => {
    openDialog(
      <div className="text-md mt-10">
        <div className="px-5 my-4 text-sm">
          Ihre Suchliste enthält{' '}
          <b className="text-red-600 text-2xl"> {results.length} </b> E-Mails
          für Turniere. Möchten Sie eine Sammel-E-Mail senden?
        </div>
      </div>,
      () => {
        resendEmailAPIcall(results);
        setDisabledEmails(new Set(results.map((r) => r.anmeldung_id)));
        setGroupDisabled(true);
        closeDialog();
      },
    );
  };

  interface EmailConfirmationResponse {
    status: 'success' | 'error';
    list?: any[]; // Replace 'any' with actual type if known
    message?: string;
  }

  const resendEmailAPIcall = async (data: EmailItem[]): Promise<void> => {
    try {
      const response = await axiosInstance.post<EmailConfirmationResponse>(
        'resend-email-confirmation',
        data,
      );

      openDialog(
        <div className="text-md text-center  mt-10">
          <img src={DoneIcon} alt="" className="mx-auto" />
          <div className="px-5 my-4 text-sm">
            E-Mail wurde erfolgreich versendet.
          </div>
        </div>,
        () => {},
        true,
      );
    } catch (e) {
      console.log(e);
    }
  };

  const getList = async (): Promise<void> => {
    try {
      setLoading(true);

      const formattedStart = startDateTime.replace('T', ' ');
      const formattedEnd = endDateTime.replace('T', ' ');

      const response = await axiosInstance.get<EmailConfirmationResponse>(
        `email-confirmation?start_date=${formattedStart}&end_date=${formattedEnd}`,
      );

      const data = response.data;

      if (data.status === 'success' && data.list) {
        setResults(data.list);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ?? 'An unexpected error occurred';

      openDialog(
        <div className="text-md mt-10">
          <img
            className="mx-auto"
            alt="tournaments not find "
            src={tournamentsEmptyState}
          />
          <div className="px-5 my-4 font-bold text-sm">⚠️ {errorMessage}</div>
        </div>,
        () => {},
        true,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6  min-h-screen">
      <section className="bg-white rounded-md border p-6 shadow-sm">
        <h2 className="text-2xl font-semibold mb-6">
          E-Mails zur Turnierbestätigung suchen
        </h2>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div>
            <label className="block mb-1 font-medium">
              Startdatum & Uhrzeit
            </label>
            <input
              type="datetime-local"
              step="1"
              value={startDateTime}
              onChange={(e) => setStartDateTime(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Enddatum & Uhrzeit</label>
            <input
              type="datetime-local"
              step="1"
              value={endDateTime}
              onChange={(e) => setEndDateTime(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Club-ID (optional)</label>
            <input
              type="text"
              value={clubId}
              onChange={(e) => setClubId(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="z. B. 840"
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">
              Turnier-ID (optional)
            </label>
            <input
              type="text"
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="z. B. 19370"
            />
          </div>

          <div className="md:col-span-2 text-right">
            <Button type="submit">
              <svg
                width="55"
                height="55"
                viewBox="0 0 55 55"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M24.0625 4.58337C20.9564 4.58364 17.8953 5.32671 15.1348 6.75059C12.3742 8.17447 9.99417 10.2379 8.19325 12.7686C6.39233 15.2994 5.22273 18.2242 4.78204 21.2989C4.34135 24.3736 4.64234 27.5091 5.65991 30.4439C6.67748 33.3786 8.38211 36.0275 10.6316 38.1694C12.8811 40.3114 15.6102 41.8843 18.5912 42.757C21.5722 43.6298 24.7187 43.7769 27.7682 43.1863C30.8176 42.5957 33.6816 41.2843 36.1212 39.3617L44.4904 47.7309C44.9226 48.1483 45.5015 48.3793 46.1024 48.3741C46.7032 48.3689 47.278 48.1279 47.7029 47.703C48.1278 47.2781 48.3688 46.7033 48.374 46.1024C48.3793 45.5016 48.1483 44.9227 47.7308 44.4905L39.3617 36.1213C41.6258 33.249 43.0356 29.7972 43.4296 26.1611C43.8236 22.5249 43.186 18.8513 41.5897 15.5607C39.9934 12.27 37.5029 9.49523 34.4032 7.5539C31.3035 5.61258 27.7199 4.58313 24.0625 4.58337ZM9.16667 24.0625C9.16667 20.1119 10.736 16.3231 13.5296 13.5296C16.3231 10.7361 20.1119 9.16671 24.0625 9.16671C28.0131 9.16671 31.8019 10.7361 34.5954 13.5296C37.389 16.3231 38.9583 20.1119 38.9583 24.0625C38.9583 28.0132 37.389 31.802 34.5954 34.5955C31.8019 37.389 28.0131 38.9584 24.0625 38.9584C20.1119 38.9584 16.3231 37.389 13.5296 34.5955C10.736 31.802 9.16667 28.0132 9.16667 24.0625Z"
                  fill="white"
                />
              </svg>
              Suchen
            </Button>
          </div>
        </form>
      </section>

      <section className="bg-white rounded-md border p-6 shadow-sm">
        <h2 className="text-xl font-semibold mb-6">Ergebnisse</h2>

        {results.length === 0 ? (
          <p className="text-gray-600">
            Keine Einträge gefunden. Bitte Suche starten.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            <div className="flex bg-blue-50 border border-blue-500 rounded-md items-center px-4 py-3 justify-between ">
              <b>
                Gesamtanzahl:{' '}
                <span className="text-red-500">{results.length}</span>
              </b>
              <Button
                className="bg-blue-600 text-white hover:bg-blue-700"
                disabled={
                  groupDisabled ||
                  disabledEmails.size > 0 ||
                  results.length === 0
                }
                onClick={handleSendAll}
              >
                <svg
                  width="51"
                  height="51"
                  viewBox="0 0 51 51"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M42.5 8.5H12.75C10.4061 8.5 8.5 10.4061 8.5 12.75V23.375H12.75V17L26.35 27.2C26.7178 27.4759 27.1652 27.625 27.625 27.625C28.0848 27.625 28.5322 27.4759 28.9 27.2L42.5 17V36.125H25.5V40.375H42.5C44.8439 40.375 46.75 38.4689 46.75 36.125V12.75C46.75 10.4061 44.8439 8.5 42.5 8.5ZM27.625 22.8438L14.1652 12.75H41.0847L27.625 22.8438Z"
                    fill="white"
                  />
                  <path
                    d="M4.25 25.5H19.125V29.75H4.25V25.5ZM8.5 31.875H21.25V36.125H8.5V31.875ZM14.875 38.25H23.375V42.5H14.875V38.25Z"
                    fill="white"
                  />
                </svg>
                Alle E-Mails senden
              </Button>
            </div>

            <div className="flex flex-col gap-4">
              {results.map((item, index) => {
                const disabled = disabledEmails.has(item.anmeldung_id);
                return (
                  <div
                    key={item.anmeldung_id + '_' + index}
                    className={`border rounded-md p-4 flex flex-col gap-2 transition-colors ${
                      disabled ? 'bg-green-100' : 'bg-gray-100'
                    }`}
                  >
                    <div className="flex relative justify-between items-start">
                      <small className="absolute -left-6 -top-6 rounded-l-lg bg-white border border-gray-300 text-gray-500 shadow-sm px-2 rounded-br-2xl">
                        {index + 1}
                      </small>
                      <div className="max-w-[80%]">
                        <div className="font-semibold">{item.subject}</div>
                        <div className="text-sm text-gray-700 mt-1">
                          Datum: {item.entry}
                        </div>
                      </div>
                      <Button
                        disabled={disabled || groupDisabled}
                        onClick={() => handleSendEmail(item)}
                        className="bg-blue-600 text-white hover:bg-blue-700 ml-4 whitespace-nowrap"
                      >
                        <svg
                          width="55"
                          height="55"
                          viewBox="0 0 55 55"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M7.79625 15.2922C7.20042 9.94347 12.7073 6.01555 17.5725 8.32097L44.9442 21.2872C50.1875 23.7691 50.1875 31.2308 44.9442 33.7126L17.5725 46.6812C12.7073 48.9866 7.20271 45.0587 7.79625 39.7099L8.89625 29.7916H27.5C28.1078 29.7916 28.6907 29.5502 29.1205 29.1204C29.5502 28.6906 29.7917 28.1077 29.7917 27.4999C29.7917 26.8921 29.5502 26.3092 29.1205 25.8795C28.6907 25.4497 28.1078 25.2083 27.5 25.2083H8.89854L7.79625 15.2922Z"
                            fill="white"
                          />
                        </svg>
                        E-Mail senden
                      </Button>
                    </div>

                    <p>
                      <strong>Empfänger:</strong> {item.recipients.join(', ')}
                    </p>

                    <p>
                      <strong>Anlage:</strong>{' '}
                      <a
                        href={`/attachments/${item.attachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        {item.attachment}
                      </a>
                    </p>

                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700">
                        E-Mail Text anzeigen
                      </summary>
                      <pre className="whitespace-pre-wrap mt-1 text-sm text-gray-800">
                        {item.body}
                      </pre>
                    </details>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ResendEmailPage;
